import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
gsap.registerPlugin(ScrollTrigger, SplitText);

import { horizontalLoop } from '../snippets/horizontalLoop';
import { onWidthResize, offWidthResize } from '../utils/resize.js';

const MAX_ITEMS = 12;
// Fallback scan (facet without a storefront filter): pages of 100 products of
// the same product type, verified by gid, until the slider is full.
const MAX_SCAN_PAGES = 5;

export default class ProductRelated {
    constructor(container) {
        this.container = container;
        // The article slider (sections/article-products.liquid) reuses this module
        // with its own wrapper, so match either section shell.
        this.section = container.closest('.section-product-related, .article-products');

        this.track = container.querySelector('.product-related__track');
        this.title = container.querySelector('.product-related__title');
        this.nextBtn = container.querySelector('.product-related__arrow--next');
        this.prevBtn = container.querySelector('.product-related__arrow--prev');

        // Each facet is "filter-param=gid", e.g.
        // filter.p.m.custom.designer=gid://shopify/Metaobject/123 (the param is
        // resolved in snippets/related-facets.liquid). Facets sharing a param
        // belong to the same filter field; every gid of every field is required.
        this.facetGroups = [];
        (container.dataset.relatedFacets || '')
            .split('|')
            .filter(Boolean)
            .forEach((pair) => {
                const i = pair.indexOf('=');
                if (i < 1) return;
                const key = pair.slice(0, i);
                const gid = pair.slice(i + 1);
                if (!gid) return;
                const group = this.facetGroups.find((entry) => entry.key === key);
                if (group) {
                    if (!group.gids.includes(gid)) group.gids.push(gid);
                } else {
                    this.facetGroups.push({ key, gids: [gid] });
                }
            });

        this.currentId = container.dataset.currentId;
        this.currentType = container.dataset.currentType || '';
        this.feedUrl = container.dataset.feedUrl;

        // Server-rendered items (the article section's theme-editor fallback) are
        // already in the track — skip the feed and go straight to the slider.
        if (this.track && this.track.children.length) {
            this.reveal();
            this.initSlider();
            return;
        }

        if (!this.track || !this.facetGroups.length || !this.feedUrl) return;

        this.load();
    }

    // Products must satisfy ALL related items at once (combinatorial, not
    // additive): ONE request carries every filter field, Shopify AND-s the
    // fields, and fetchFeed then keeps only products referencing every gid -
    // so two values of one field are also AND-ed, never OR-ed.
    load = async () => {
        const matches = await this.fetchFeed(this.facetGroups);

        // null = the request failed. Never treat that as "no products" — just
        // drop the section quietly.
        if (matches === null) {
            this.section?.remove();
            return;
        }

        // Exclude the current product and de-dupe by product id.
        const seen = new Set([this.currentId]);
        const items = [];
        matches.forEach((node) => {
            const id = node.querySelector('.product-card')?.dataset.productId;
            if (!id || seen.has(id)) return;
            seen.add(id);
            items.push(node);
        });

        const capped = items.slice(0, MAX_ITEMS);
        if (!capped.length) {
            this.section?.remove();
            return;
        }

        capped.forEach((item) => this.track.appendChild(item));
        this.reveal();
        this.initSlider();
    };

    // Metaobject GIDs a feed item references across every facet metafield.
    facetGidsOf = (node) => (node.dataset.facetGids || '').split(' ').filter(Boolean);

    // A product item matches when it references EVERY gid of EVERY facet
    // group (strict AND over all related items). Shopify OR-s several values
    // of one filter field, so a filtered page is a superset of this.
    matchesAll = (item, groups) => {
        const gids = this.facetGidsOf(item);
        return groups.every((group) => group.gids.every((gid) => gids.includes(gid)));
    };

    // One feed request. Resolves to { items, active, next } — the product
    // item nodes, how many storefront filters Shopify actually applied, and
    // the next page URL — or null when the request fails.
    requestFeed = async (url) => {
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const meta = doc.querySelector('[data-feed-meta]');
        return {
            items: Array.from(doc.querySelectorAll('.product-related__item')),
            active: parseInt(meta?.dataset.activeFilters || '0', 10),
            next: meta?.dataset.next || '',
        };
    };

    // Returns the matching product-item nodes, or null when the request fails.
    //
    // 1. One request carrying every facet as a storefront filter; Shopify
    //    AND-s the fields and the result is verified by gid.
    // 2. A facet whose metafield is not an enabled filter (Search & Discovery
    //    caps the store at 25) is silently ignored by Shopify, so the page
    //    comes back less narrowed than asked - the feed meta says how many
    //    filters really applied. Then the product's own type is scanned
    //    through the Product type filter (which is enabled), page by page,
    //    and every item is verified by gid, so e.g. a Magazine Title still
    //    yields the other issues of that magazine.
    fetchFeed = async (groups) => {
        const url = new URL(this.feedUrl, window.location.origin);
        groups.forEach(({ key, gids }) => {
            gids.forEach((gid) => url.searchParams.append(key, gid));
        });

        try {
            const first = await this.requestFeed(url);
            const matches = first.items.filter((item) => this.matchesAll(item, groups));
            if (first.active >= groups.length) return matches;

            const scanUrl = new URL(this.feedUrl, window.location.origin);
            if (this.currentType) scanUrl.searchParams.set('filter.p.product_type', this.currentType);
            let page = 1;
            let next = scanUrl.toString();
            while (next && page <= MAX_SCAN_PAGES && matches.length < MAX_ITEMS + 1) {
                const result = await this.requestFeed(new URL(next, window.location.origin));
                result.items.forEach((item) => {
                    if (this.matchesAll(item, groups)) matches.push(item);
                });
                next = result.next;
                page += 1;
            }
            return matches;
        } catch (error) {
            const label = groups.map((group) => group.key).join(' + ');
            console.warn(`product-related: failed to load facets ${label}`, error);
            return null;
        }
    };

    reveal = () => {
        this.section?.removeAttribute('hidden');
        this.container.classList.remove('product-related--loading');
        // Recalculate triggers now that the section occupies space.
        ScrollTrigger.refresh();
    };

    initSlider = () => {
        this.boxes = gsap.utils.toArray(this.container.querySelectorAll('.product-related__item'));

        // Only build the draggable/looping slider when the cards overflow the row;
        // with few siblings they fit and stay static (no loop, no arrows, no drag).
        if (this.overflows()) {
            // paused: true → no autoplay; advances only via arrows/drag.
            this.loop = horizontalLoop(this.boxes, {
                gap: 20,
                paused: true,
                draggable: true,
                center: false,
                repeat: -1,
            });
            this.bindControls();
        } else {
            this.container.classList.add('product-related--static');
        }

        if (this.title) {
            this.titleSplit = new SplitText(this.title, { type: 'lines,words', linesClass: 'lineParent' });
            gsap.fromTo(
                this.titleSplit.words,
                { yPercent: 100 },
                {
                    yPercent: 0,
                    duration: 0.6,
                    stagger: { each: 0.1, from: 'start' },
                    ease: 'power3.out',
                    scrollTrigger: { trigger: this.container },
                    onComplete: () => onWidthResize(this.onResize),
                }
            );
        }

        gsap.set(this.boxes, { opacity: 0, yPercent: 40 });
        ScrollTrigger.batch(this.boxes, {
            interval: 0.2,
            batchMax: 7,
            start: 'top-=40px bottom',
            toggleActions: 'play pause resume reset',
            onEnter: (elements) => {
                gsap.to(elements, {
                    opacity: 1,
                    yPercent: 0,
                    duration: 1.6,
                    stagger: { each: 0.1 },
                    overwrite: 'auto',
                    ease: 'expo.out',
                });
            },
        });
    };

    bindControls = () => {
        this.boxes.forEach((box, i) => {
            box.addEventListener('click', () => this.loop.toIndex(i, { duration: 0.8, ease: 'power1.inOut' }));
        });
        this.nextBtn?.addEventListener('click', () => this.loop.next({ duration: 0.4, ease: 'power1.inOut' }));
        this.prevBtn?.addEventListener('click', () => this.loop.previous({ duration: 0.4, ease: 'power1.inOut' }));
    };

    overflows = () => this.track.scrollWidth > this.track.clientWidth + 1;

    onResize = () => {
        this.titleSplit?.revert();
        offWidthResize(this.onResize);
    };
}
